import { Box, Flex, Heading } from '@chakra-ui/react';
import React, { useCallback, useMemo, useState } from 'react';

import { date as commonDate } from '@finley-technologies/common';

import {
  useGetInvoicesByOrganizationIdPaginatedQuery,
  useGetInvoiceAggregatesByOrganizationIdQuery,
  useGetInvoicesByOrganizationIdPaginatedLazyQueryGQLType,
} from '../requests/invoices.gql.generated';
import { CreateInvoiceButton } from './create/CreateInvoiceButton';
import {
  InvoicesOverviewCards,
  InvoiceAggregatesData,
} from './InvoicesOverviewCards';
import { InvoicesTable } from './InvoicesTable';
import {
  getInvoicesWithComputedStatus,
  HighlightComputedStatus,
  getInvoiceOverviewInitialDateRange,
} from './utils';
import { IsolatedMultiSelect } from 'packages/common-frontend/components/MultiSelect';
import { ChartDateRange } from 'packages/loanProduct/ui/receivablesDashboard/utils';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { MultiEntityDrawer as InvoicesEventLogMultiEntityDrawer } from '~/core/routes/invoices/EntityEventLogDrawers';
import { FullDateRangePicker } from '~/packages/common-frontend/components/DateRangePicker';

const DEFAULT_PAGE_SIZE = 25;

export const InvoicesOverview = () => {
  const { user } = useUserAuthOrThrow();
  const { facilities } = useCreditFacilitiesOrThrow();

  const creditFacilityIdsAndOptions = useMemo(() => {
    const creditFacilityIds: string[] = [];
    const creditFacilityOptions: { value: string; label: string }[] = [];

    facilities.forEach((facility) => {
      creditFacilityIds.push(facility.id);
      creditFacilityOptions.push({ value: facility.id, label: facility.name });
    });

    return {
      creditFacilityIds,
      creditFacilityOptions,
    };
  }, [facilities]);

  const initialDateRange = useMemo(
    () => getInvoiceOverviewInitialDateRange(),
    [],
  );

  const [
    selectedComputedStatus,
    setSelectedComputedStatus,
  ] = useState<HighlightComputedStatus | null>(null);
  const [selectedDateRange, setSelectedDateRange] = useState<ChartDateRange>(
    initialDateRange,
  );
  const [selectedCreditFacilityIds, setSelectedCreditFacilityIds] = useState<
    string[]
  >(creditFacilityIdsAndOptions.creditFacilityIds);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Sorting state for server-side sorting
  const [sortBy, setSortBy] = useState<
    'dueDate' | 'created_at' | 'totalAmount' | 'status'
  >('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const onSelectDateRange: (
    newDateRange: [Date | null, Date | null],
  ) => void = useCallback(
    (newDateRange: [Date | null, Date | null]) => {
      const [newStartDate, newEndDate] = newDateRange;

      if (newStartDate && newEndDate) {
        setSelectedDateRange({
          chartDataStartDate: newStartDate,
          chartDataEndDate: newEndDate,
        });
      }

      // if dates cleared, pass in the initial date range conditions
      if (!newStartDate && !newEndDate) {
        setSelectedDateRange({
          chartDataStartDate: initialDateRange.chartDataStartDate,
          chartDataEndDate: initialDateRange.chartDataEndDate,
        });
      }

      // Reset to first page when filters change
      setCurrentPage(1);
    },
    [initialDateRange],
  );

  // Convert date range to FullDateStr format for server queries
  const startDateStr = useMemo(
    () => commonDate.dateToFullDateString(selectedDateRange.chartDataStartDate),
    [selectedDateRange.chartDataStartDate],
  );
  const endDateStr = useMemo(
    () => commonDate.dateToFullDateString(selectedDateRange.chartDataEndDate),
    [selectedDateRange.chartDataEndDate],
  );

  // Fetch aggregates from server (for the summary cards)
  const {
    data: aggregatesData,
    loading: isAggregatesLoading,
  } = useGetInvoiceAggregatesByOrganizationIdQuery({
    variables: {
      organizationId: user.organizationId,
      startDate: startDateStr,
      endDate: endDateStr,
      creditFacilityIds: selectedCreditFacilityIds,
    },
    fetchPolicy: 'cache-and-network',
  });

  // Fetch paginated invoices for the table
  const {
    data: paginatedInvoicesData,
    loading: isInvoicesLoading,
  } = useGetInvoicesByOrganizationIdPaginatedQuery({
    variables: {
      organizationId: user.organizationId,
      pagination: {
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
      },
      creditFacilityIds: selectedCreditFacilityIds,
      startDate: startDateStr,
      endDate: endDateStr,
      sortBy,
      sortDirection,
      computedStatuses: selectedComputedStatus
        ? [selectedComputedStatus]
        : undefined,
    },
    fetchPolicy: 'cache-and-network',
  });

  // Lazy query to fetch ALL invoices matching current filters for download
  const [
    fetchAllInvoicesForDownload,
    { loading: isDownloadLoading },
  ] = useGetInvoicesByOrganizationIdPaginatedLazyQueryGQLType({
    fetchPolicy: 'network-only',
  });

  // Transform paginated invoices for display
  const invoicesWithComputedStatus = useMemo(() => {
    return getInvoicesWithComputedStatus({
      invoices:
        paginatedInvoicesData?.getInvoicesByOrganizationIdPaginated?.nodes,
    });
  }, [paginatedInvoicesData?.getInvoicesByOrganizationIdPaginated?.nodes]);

  // Get pagination info
  const paginationInfo = useMemo(() => {
    const data = paginatedInvoicesData?.getInvoicesByOrganizationIdPaginated;
    if (!data) return null;

    const totalCount = data.totalCount;
    const totalPages = Math.ceil(totalCount / pageSize);

    return {
      totalCount,
      totalPages,
      hasNextPage: data.pageInfo.hasNextPage,
      hasPreviousPage: data.pageInfo.hasPreviousPage,
    };
  }, [paginatedInvoicesData?.getInvoicesByOrganizationIdPaginated, pageSize]);

  // Aggregates for the cards
  const aggregates: InvoiceAggregatesData | undefined = useMemo(() => {
    const data = aggregatesData?.getInvoiceAggregatesByOrganizationId;
    if (!data) return undefined;
    return data;
  }, [aggregatesData?.getInvoiceAggregatesByOrganizationId]);

  // Pagination handlers
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  }, []);

  // Reset to page 1 when credit facility filter changes
  const handleCreditFacilityFilterChange = useCallback((ids: string[]) => {
    setSelectedCreditFacilityIds(ids);
    setCurrentPage(1);
  }, []);

  // Reset to page 1 when computed status filter changes
  const handleComputedStatusChange = useCallback(
    (status: HighlightComputedStatus | null) => {
      setSelectedComputedStatus((prev) => (prev === status ? null : status));
      setCurrentPage(1);
    },
    [],
  );

  // Download handler - fetches all matching invoices
  const handleDownloadAll = useCallback(async (): Promise<
    ReturnType<typeof getInvoicesWithComputedStatus> | undefined
  > => {
    const totalCount =
      paginatedInvoicesData?.getInvoicesByOrganizationIdPaginated?.totalCount ??
      0;
    if (totalCount === 0) return undefined;

    const result = await fetchAllInvoicesForDownload({
      variables: {
        organizationId: user.organizationId,
        pagination: {
          limit: totalCount,
          offset: 0,
        },
        creditFacilityIds: selectedCreditFacilityIds,
        startDate: startDateStr,
        endDate: endDateStr,
        sortBy,
        sortDirection,
        computedStatuses: selectedComputedStatus
          ? [selectedComputedStatus]
          : undefined,
      },
    });

    return getInvoicesWithComputedStatus({
      invoices: result.data?.getInvoicesByOrganizationIdPaginated?.nodes,
    });
  }, [
    paginatedInvoicesData,
    fetchAllInvoicesForDownload,
    user.organizationId,
    selectedCreditFacilityIds,
    startDateStr,
    endDateStr,
    sortBy,
    sortDirection,
    selectedComputedStatus,
  ]);

  // Server-side sorting handler
  const handleSortChange = useCallback(
    (newSortBy: string, newSortDirection: 'asc' | 'desc') => {
      // Map column ids to valid server sort fields
      const sortFieldMap: Record<
        string,
        'dueDate' | 'created_at' | 'totalAmount' | 'status'
      > = {
        dueDate: 'dueDate',
        created_at: 'created_at',
        totalAmount: 'totalAmount',
        status: 'status',
        computedStatus: 'status', // Map computedStatus column to status field
      };

      const mappedSortBy = sortFieldMap[newSortBy];
      if (mappedSortBy) {
        setSortBy(mappedSortBy);
        setSortDirection(newSortDirection);
        setCurrentPage(1); // Reset to first page when sorting changes
      }
    },
    [],
  );

  return (
    <Box>
      <Flex alignItems="center" justifyContent="space-between" mb="40px">
        <Heading display="inline-block">Invoices</Heading>
        <Flex gap="0.5rem">
          <InvoicesEventLogMultiEntityDrawer />
          <CreateInvoiceButton />
        </Flex>
      </Flex>

      <Flex alignItems="center" gap="24px" mb="24px">
        <FullDateRangePicker
          onSelectDateRange={onSelectDateRange}
          currentDateRange={{
            startDate: selectedDateRange.chartDataStartDate,
            endDate: selectedDateRange.chartDataEndDate,
          }}
          width="50%"
        />
        <IsolatedMultiSelect
          applyButtonText="Filter"
          buttonProps={{
            width: '50%',
          }}
          counterLabel="Credit facilities"
          initialSelections={selectedCreditFacilityIds}
          isSearchable
          menuListProps={{ zIndex: 'dropdown' }}
          onApplyOptions={handleCreditFacilityFilterChange}
          options={creditFacilityIdsAndOptions.creditFacilityOptions}
          placeholderText="Select credit facilities"
          showSelectedCount
          titleText="All credit facilities"
        />
      </Flex>

      <InvoicesOverviewCards
        aggregates={aggregates}
        isLoading={isAggregatesLoading}
        selectedComputedStatus={selectedComputedStatus}
        setSelectedComputedStatus={handleComputedStatusChange}
      />

      <InvoicesTable
        invoices={invoicesWithComputedStatus}
        isLoading={isInvoicesLoading}
        pagination={
          paginationInfo
            ? {
                currentPage,
                totalPages: paginationInfo.totalPages,
                pageSize,
                totalCount: paginationInfo.totalCount,
                hasNextPage: paginationInfo.hasNextPage,
                hasPreviousPage: paginationInfo.hasPreviousPage,
                onPageChange: handlePageChange,
                onPageSizeChange: handlePageSizeChange,
              }
            : undefined
        }
        serverSorting={{
          sortBy,
          sortDirection,
          onSortChange: handleSortChange,
        }}
        onDownloadAll={handleDownloadAll}
        isDownloadLoading={isDownloadLoading}
      />
    </Box>
  );
};
